/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import {
    App,
    delFramework,
    Framework,
    getFramework,
    StoreProvider,
    type History,
    type ServiceManager,
} from '@/framework';
import { type CustomDataService } from '@/framework/services/custom-data';
import type { Layer } from '@/layers/layer';
import type { IEditorConfig, ILayerProps, IRegisterPluginOptions, PosterType } from '@/version-types';
import { loadInnerPlugins } from './load-inner-plugins';

export class Editor extends Framework {
    private __container: HTMLElement | null;
    private __root: Root | null = null;
    private __isLoaded = false;
    /** 是否由组件初始化 */
    private __fromComponentInit = false;
    /** 编辑器 ID */
    id: string;
    /** 基础图层 */
    layer: Layer;
    /** 历史记录 */
    history: History;
    /** 自定义图层 */
    customLayer: CustomDataService;
    /** 服务管理器 */
    services: ServiceManager;

    constructor(id: string, config?: IEditorConfig & { padding?: number | number[] }) {
        super(id);
        this.id = id;
        this.__container = null;
        this.framework.service.setConfig({ ...config });
        if (config?.padding !== undefined) {
            this.framework.service.edgelessCanvas.setFitPadding(config.padding);
        }
        // 加载内置插件
        loadInnerPlugins(this.registerPlugins.bind(this));
        // 挂载内置实例
        this.history = this.framework.service.history;
        this.services = this.framework.service;
        this.customLayer = this.framework.service.customData;

        this.layer = this.getInstance<Layer>('Layer');
    }

    /** 设置是否由组件初始化 */
    setFromComponentInit(fromComponentInit: boolean) {
        this.__fromComponentInit = fromComponentInit;
    }

    /** 捕获错误 */
    captureException(error: Error, options?: { extra?: Record<string, unknown>; level?: 'error' | 'log' }) {
        this.framework.service.captureException(error, options);
    }

    /** 捕获消息 */
    captureMessage(message: string, options?: { extra?: Record<string, unknown>; level?: 'error' | 'log' }) {
        this.framework.service.captureMessage(message, options);
    }

    /** 获取预览模式 ID */
    getPreviewModeWrapperId = () => this.framework.service.getPreviewModeId();

    /** 初始化数据 */
    init() {
        // 框架模块初始化
        this.framework.service.init();
    }

    /** 获取图层行为 */
    getBehavior(layer: ILayerProps) {
        return this.framework.service.behavior.get(layer);
    }

    /**
     * 注册插件
     *
     * @param configs - 插件配置
     */
    registerPlugins(configs: IRegisterPluginOptions[]) {
        this.framework.service.registration.registerPlugins(configs);
    }

    /**
     * 注册实例
     *
     * @param configs - 实例配置
     */
    registerInstance<T extends Record<string, any>>(configs: T): void {
        Object.entries(configs).forEach(([key, instance]) => {
            this[key] = instance;
        });
    }

    /**
     * 根据类名获取实例 - 实现
     */
    getInstance<T = any>(key: string): T {
        return this.framework.service.registration.getInstance<T>(key);
    }

    /** 获取配置 */
    getConfig() {
        return this.framework.service.getConfig();
    }

    /** 设置用户唯一标识 */
    setUid(uid: number) {
        this.framework.service.editor.setUid(uid);
    }

    /** 取消事件监听 */
    off(handler: number) {
        this.framework.service.events.off(handler);
    }

    on(event: string, fn: (...args: any[]) => void, weight = 0) {
        return this.framework.service.events.on(event, fn, weight);
    }

    emit(eventName: string, ...args: any[]) {
        return this.framework.service.events.emit(eventName, ...args);
    }

    /** 触发快捷键 */
    emitHotkey(key: string) {
        return this.framework.service.hotkey.__emit(key);
    }

    /** 监听快捷键 */
    onHotkey(key: string, fn: () => void, weight = 0) {
        return this.framework.service.hotkey.on(key, fn, weight);
    }

    // TODO: 待确认这里是否只替换原来的 LAYER_BASE_EVENT.delete，LAYER_BASE_EVENT.delete 的定义是图层删除，onDelete 目前的实现还只是 Delete 键的侦听触发
    /** 监听键盘删除（与 HotkeyService 一致：回调接收来源信息，返回 true 可阻止后续监听） */
    onDelete(fn: (args?: Record<string, any>) => void | boolean, weight = 0) {
        return this.framework.service.hotkey.onDelete(fn, weight);
    }

    /** 触发删除（字符串会转为 `{ from }`，与 `__emitDelete` 一致） */
    emitDelete(from?: string | { from: string }) {
        const args = typeof from === 'string' ? { from } : from;
        return this.framework.service.hotkey.__emitDelete(args);
    }

    /** 监听esc */
    onEsc(fn: () => void, weight = 0) {
        return this.framework.service.hotkey.onEsc(fn, weight);
    }

    /** 监听回车 */
    onEnter(fn: () => void, weight = 0) {
        return this.framework.service.hotkey.onEnter(fn, weight);
    }

    /** 监听space */
    onSpace(fn: (args: { isKeydown: boolean; isKeyup: boolean }) => void, weight = 0) {
        return this.framework.service.hotkey.onSpace(fn, weight);
    }

    /** 监听alt */
    onAlt(fn: (args: { isKeydown: boolean; isKeyup: boolean }) => void, weight = 0) {
        return this.framework.service.hotkey.onAlt(fn, weight);
    }

    /** 监听鼠标按下 */
    onMouseDown(fn: (e: React.MouseEvent) => void, weight = 0) {
        return this.framework.service.editor.onMouseDown(fn, weight);
    }

    /** 获取缩放 */
    getScale() {
        return this.framework.service.edgelessCanvas.zoom;
    }

    /** 设置缩放 */
    setScale(scale: number) {
        this.framework.service.edgelessCanvas.setZoom(scale);
    }

    /** 设置是否自动缩放 */
    setIsAutoScale(isAutoScale: boolean) {
        this.framework.service.edgelessCanvas.setIsAutoScale(isAutoScale);
    }

    /** 监听缩放变化 */
    onScale(fn: (scale: number) => void) {
        return this.framework.service.edgelessCanvas.onZoom(fn);
    }

    /** 监听基础操作状态变化 */
    onBaseOperateChange(fn: (isBaseOperate: boolean) => void) {
        const handler = this.framework.service.editor.onIsBaseOperateChange(fn);
        return () => this.framework.service.events.off(handler);
    }

    /** 设置自动缩放 */
    setAutoScale(autoScale: boolean) {
        this.framework.service.edgelessCanvas.setIsAutoScale(autoScale);
    }

    /** 放大 */
    zoomIn() {
        this.framework.service.edgelessCanvas.zoomIn();
    }

    /** 缩小 */
    zoomOut() {
        this.framework.service.edgelessCanvas.zoomOut();
    }

    /** 设置主题 */
    setTheme(theme: string) {
        this.framework.service.editor.setTheme(theme);
    }
    /** 获取主题 */
    getTheme() {
        return this.framework.service.editor.getTheme();
    }

    /** 设置海报类型 */
    setPosterType(posterType: PosterType) {
        this.framework.service.editor.setPosterType(posterType);
    }

    /** 设置容器尺寸 */
    setViewport(width: number, height: number) {
        this.framework.service.edgelessCanvas.setSize({ width, height });
    }

    /** 禁用全部操作 */
    disableOperate() {
        this.framework.service.editor.setDisableOperate(true);
    }

    /** 启用全部操作 */
    enableOperate() {
        this.framework.service.editor.setDisableOperate(false);
    }

    /** 禁用基础操作 */
    disableBasicOperate(operate: {
        pick?: boolean;
        move?: boolean;
        resize?: boolean;
        rotate?: boolean;
        scale?: boolean;
        active?: boolean;
    }) {
        if (operate.pick) {
            this.disablePick();
        }
        if (operate.move) {
            this.disableMove();
        }
        if (operate.resize) {
            this.disableResize();
        }
        if (operate.rotate) {
            this.disableRotate();
        }
        if (operate.scale) {
            this.disableScale();
        }
        if (operate.active) {
            this.disableActive();
        }
    }

    /** 启用基础操作 */
    enableBasicOperate(operate: {
        pick?: boolean;
        move?: boolean;
        resize?: boolean;
        rotate?: boolean;
        scale?: boolean;
    }) {
        if (operate.pick) {
            this.enablePick();
        }
        if (operate.move) {
            this.enableMove();
        }
        if (operate.resize) {
            this.enableResize();
        }
        if (operate.rotate) {
            this.enableRotate();
        }
        if (operate.scale) {
            this.enableScale();
        }
    }

    /** 禁用选中 */
    disablePick(delActiveLayer = true) {
        this.framework.service.editor.setDisablePick(true);
        if (delActiveLayer) {
            this.framework.service.frame.delActiveLayer();
        }
    }

    /** 启用选中 */
    enablePick() {
        this.framework.service.editor.setDisablePick(false);
    }

    /** 启用全部基础操作 */
    enableAllBaseOperate() {
        this.framework.service.editor.setDisableAllBaseOperate(false);
    }

    /** 禁用全部基础操作 */
    disableAllBaseOperate() {
        this.framework.service.editor.setDisableAllBaseOperate(true);
    }

    /** 禁用移动 */
    disableMove() {
        this.framework.service.editor.setDisableMove(true);
    }

    /** 启用移动 */
    enableMove() {
        this.framework.service.editor.setDisableMove(false);
    }

    /** 禁用缩放 */
    disableScale() {
        this.framework.service.editor.setDisableScale(true);
    }

    /** 启用缩放 */
    enableScale() {
        this.framework.service.editor.setDisableScale(false);
    }

    /** 禁用旋转 */
    disableRotate() {
        this.framework.service.editor.setDisableRotate(true);
    }

    /** 启用旋转 */
    enableRotate() {
        this.framework.service.editor.setDisableRotate(false);
    }

    /** 禁用尺寸调整 */
    disableResize() {
        this.framework.service.editor.setDisableResize(true);
    }

    /** 启用尺寸调整 */
    enableResize() {
        this.framework.service.editor.setDisableResize(false);
    }

    /** 禁用激活 */
    disableActive() {
        this.framework.service.editor.setDisableActive(true);
    }

    /** 启用激活 */
    enableActive() {
        this.framework.service.editor.setDisableActive(false);
    }

    /** 禁用鼠标选择 */
    disableMouseSelect() {
        this.framework.service.editor.setDisableMouseSelect(true);
    }

    /** 启用鼠标选择 */
    enableMouseSelect() {
        this.framework.service.editor.setDisableMouseSelect(false);
    }

    /** 是否禁用全部操作 */
    isDisabled() {
        return this.framework.service.editor.disableOperate;
    }

    /** 设置编辑器只读 */
    setReadonly(readonly: boolean) {
        this.framework.service.frame.delActiveLayer();
        this.framework.service.editor.setReadonly(readonly);
        this.history.setRedoUndo(!readonly);
    }

    /** 获取编辑器只读状态 */
    getReadonly(): boolean {
        return this.framework.service.editor.getReadonly();
    }

    /** 监听编辑器状态 */
    onState(
        fn: (args: {
            disableOperate: boolean;
            disablePick: boolean;
            disableMouseSelect: boolean;
            disableMove: boolean;
            disableScale: boolean;
            disableRotate: boolean;
            disableResize: boolean;
            isBaseOperate: boolean;
            isMouseSelect: boolean;
        }) => void,
    ) {
        const handle = this.framework.service.editor.onStateChange(fn);
        return () => this.framework.service.events.off(handle);
    }

    /** 监听图片合成失败 */
    onBehaviorErrorImage(fn: (error: { layerId: string; imageUrl: string }) => void) {
        const handler = this.framework.service.onImageError(fn);

        return () => {
            this.framework.service.events.off(handler);
        };
    }

    /** 挂载 */
    create() {
        if (!this.__isLoaded) {
            this.__isLoaded = true;
            this.framework.service.create();
        }
    }

    /** 挂载 */
    mount() {
        if (!this.__container) {
            this.__container = document.getElementById(this.id)!;
        }
        if (!this.__container) {
            throw new Error(`container not found: ${this.id}`);
        }
        this.create();
        this.framework.service.mount();

        if (this.__fromComponentInit) return;

        if (this.__container) {
            this.__root = this.__root || createRoot(this.__container);
            this.__root.render(
                <StoreProvider state={this.framework.service.registration.states}>
                    <App />
                </StoreProvider>,
            );
        }
    }

    /** 卸载 */
    unmount() {
        this.framework.service.unmount();

        if (this.__fromComponentInit) return;
        if (this.__container) {
            this.__root?.unmount();
            this.__root = null;
        }
    }

    /** 销毁 */
    destroy() {
        delFramework(this.id);
        this.unmount();
        this.__container = null;
    }
}

/** 组件渲染 */
export const EditorComponent: React.FC<{
    editor: Editor;
    className?: string;
}> = React.memo(({ editor, className = '' }) => {
    const framework = getFramework(editor.id);
    editor.setFromComponentInit(true);

    console.log('framework==>', framework.service.registration.states);

    return (
        <div id={editor.id} className={className} data-component="editor" style={{ position: 'fixed' }}>
            <StoreProvider state={framework.service.registration.states}>
                <App />
            </StoreProvider>
        </div>
    );
});
